import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { 
  HomeIcon, 
  MagnifyingGlassIcon, 
  PhotoIcon, 
  TagIcon,
  ArrowsRightLeftIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  CloudArrowUpIcon,
  ArchiveBoxIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Hero = styled.div`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.xl}`};
  background: linear-gradient(
    135deg,
    ${({ theme }) => `${theme.colors.primary}05`} 0%,
    ${({ theme }) => `${theme.colors.secondary}05`} 100%
  );
  border-radius: 24px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.2;
  font-family: 'Gagalin', sans-serif;
  font-weight: 400;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.25rem;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.heading};
  opacity: 0.8;
  font-weight: 400;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-family: 'Gagalin', sans-serif;
  font-weight: 400;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

const Section = styled.section`
  margin: ${({ theme }) => theme.spacing.xxl} 0;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: ${({ delay }) => delay || '0'}s;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: ${({ theme }) => theme.spacing.xl} 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.xl};
  line-height: 1.2;
  font-family: 'Gagalin', sans-serif;
  font-weight: 400;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.75rem;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const StepCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
`;

const StepIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const StepTitle = styled.h3`
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: 1.25rem;
  font-family: 'Gagalin', sans-serif;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const StepDescription = styled.p`
  color: ${({ theme }) => theme.colors.heading};
  opacity: 0.7;
  line-height: 1.6;
`;

const FeatureHighlight = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
  align-items: center;
  margin: ${({ theme }) => theme.spacing.xxl} 0;
  position: relative;
  
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing.xxxl};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
    
    &:nth-child(even) {
      div:first-child {
        order: 2;
      }
      div:last-child {
        order: 1;
      }
    }
  }
`;

const FeatureContent = styled.div`
  position: relative;
  z-index: 1;

  h3 {
    font-size: 3rem;
    color: ${({ theme }) => theme.colors.heading};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    line-height: 1.1;
    font-family: 'Gagalin', sans-serif;
    
    span {
      display: block;
      font-size: 1.5rem;
      color: ${({ theme }) => theme.colors.primary};
      margin-bottom: ${({ theme }) => theme.spacing.xs};
      text-transform: uppercase;
      letter-spacing: 2px;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 2.5rem;
      text-align: center;
      
      span {
        font-size: 1.25rem;
      }
    }
  }

  p {
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.9;
    line-height: 1.8;
    font-size: 1.1rem;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    position: relative;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      text-align: center;
      font-size: 1rem;
    }
  }

  ${Button} {
    background: transparent;
    border: 2px solid ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xl}`};
    
    &:hover {
      background: ${({ theme }) => theme.colors.primary};
      color: white;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      margin: 0 auto;
      display: block;
    }
  }
`;

const FeatureImage = styled.div`
  position: relative;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: -20%;
    left: -20%;
    right: -20%;
    bottom: -20%;
    background: ${({ index }) => `
      radial-gradient(
        circle at ${index % 2 === 0 ? 'right' : 'left'} center,
        ${({ theme }) => `${theme.colors.primary}15`} 0%,
        transparent 70%
      )
    `};
    z-index: -1;
  }

  svg {
    width: 250px;
    height: 250px;
    color: ${({ theme }) => theme.colors.primary};
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15));
    transition: all 0.5s ease;
    
    &:hover {
      transform: translateY(-10px) scale(1.05);
      filter: drop-shadow(0 20px 25px rgba(0, 0, 0, 0.2));
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 300px;
    
    svg {
      width: 180px;
      height: 180px;
    }
  }
`;

const InsuranceSection = styled(Section)`
  background: linear-gradient(135deg,
    ${({ theme }) => `${theme.colors.primary}05`} 0%,
    ${({ theme }) => `${theme.colors.error}05`} 100%
  );
  border-radius: 24px;
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const InsuranceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const InsuranceCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  svg {
    width: 100%;
    height: 100%;
  }
`;

const FeatureTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.heading};
  font-size: 1.25rem;
  font-family: 'Gagalin', sans-serif;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
  }
`;

const BottomCTA = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xxl};
  
  ${Button} {
    font-size: 1.25rem;
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      width: 100%;
      max-width: 300px;
      font-size: 1.1rem;
    }
  }
`;

const ColoredText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Home = () => {
  return (
    <Container>
      <Hero>
        <Title>Home Inventory</Title>
        <Subtitle>at your fingertips</Subtitle>
        <ButtonGroup>
          <Button as={Link} to="/signup" variant="primary">
            Get Started
          </Button>
          <Button as={Link} to="/pricing" variant="secondary">
            View Pricing
          </Button>
        </ButtonGroup>
      </Hero>

      <Section delay={0.5}>
        <SectionTitle>How It Works</SectionTitle>
        <StepsContainer>
          <StepCard>
            <StepNumber>1</StepNumber>
            <StepIcon>
              <HomeIcon />
            </StepIcon>
            <StepTitle>Create a Room</StepTitle>
            <StepDescription>
              Start by creating virtual rooms that match your home's layout
            </StepDescription>
          </StepCard>

          <StepCard>
            <StepNumber>2</StepNumber>
            <StepIcon>
              <ArchiveBoxIcon />
            </StepIcon>
            <StepTitle>Add Virtual Bins</StepTitle>
            <StepDescription>
              Organize your space with virtual storage bins
            </StepDescription>
          </StepCard>

          <StepCard>
            <StepNumber>3</StepNumber>
            <StepIcon>
              <ListBulletIcon />
            </StepIcon>
            <StepTitle>Track Items</StepTitle>
            <StepDescription>
              Add items to bins with photos and details
            </StepDescription>
          </StepCard>
        </StepsContainer>
      </Section>

      <Section delay={1}>
        <SectionTitle>Powerful Features</SectionTitle>
        <FeatureHighlight>
          <FeatureContent>
            <h3>
              <span>Lightning Fast</span>
              Smart Search
            </h3>
            <p>
              Instantly find any item across all your rooms and bins with our 
              intelligent search system. Filter by bin, item, or tags
              to locate exactly what you need in seconds.
            </p>
            {/* <Button as={Link} to="/features">
              Learn More →
            </Button> */}
          </FeatureContent>
          <FeatureImage index={1}>
            <MagnifyingGlassIcon />
          </FeatureImage>
        </FeatureHighlight>

        <FeatureHighlight>
          <FeatureImage index={2}>
            <PhotoIcon />
          </FeatureImage>
          <FeatureContent>
            <h3>
              <span>Complete Documentation</span>
              Visual Records
            </h3>
            <p>
              Create a comprehensive digital archive with high-quality photos, 
              receipts, and warranty information. Perfect for insurance claims 
              and maintaining detailed records of your valuables.
            </p>
            {/* <Button as={Link} to="/features">
              See Examples →
            </Button> */}
          </FeatureContent>
        </FeatureHighlight>

        <FeatureHighlight>
          <FeatureContent>
            <h3>
              <span>Intuitive Interface</span>
              Easy Organization
            </h3>
            <p>
              Effortlessly reorganize your inventory. Plan your physical organization virtually, saving 
              time and effort in the real world.
            </p>
            {/* <Button as={Link} to="/features">
              Explore More →
            </Button> */}
          </FeatureContent>
          <FeatureImage index={3}>
            <ArrowsRightLeftIcon />
          </FeatureImage>
        </FeatureHighlight>
      </Section>

      <InsuranceSection delay={1.5}>
        <SectionTitle><ColoredText>BIN</ColoredText>SURANCE: Be Prepared</SectionTitle>
        <InsuranceGrid>
          <InsuranceCard>
            <FeatureIcon>
              <ShieldCheckIcon />
            </FeatureIcon>
            <FeatureTitle>Insurance Ready</FeatureTitle>
            <FeatureDescription>
              Maintain a detailed inventory of your belongings for insurance claims
            </FeatureDescription>
          </InsuranceCard>

          <InsuranceCard>
            <FeatureIcon>
              <DocumentDuplicateIcon />
            </FeatureIcon>
            <FeatureTitle>Complete Records</FeatureTitle>
            <FeatureDescription>
              Store photos, receipts, and item details in one secure place
            </FeatureDescription>
          </InsuranceCard>

          <InsuranceCard>
            <FeatureIcon>
              <CloudArrowUpIcon />
            </FeatureIcon>
            <FeatureTitle>Always Available</FeatureTitle>
            <FeatureDescription>
              Access your inventory from anywhere, ensuring your records are safe
            </FeatureDescription>
          </InsuranceCard>
        </InsuranceGrid>
      </InsuranceSection>

      <BottomCTA>
        <Button as={Link} to="/signup" variant="primary">
          Try for Free
        </Button>
      </BottomCTA>
    </Container>
  );
};

export default Home; 